<template>
  <!-- AC-1937: editor for conditional custom response headers. Each rule pairs a
       DynamicRule condition set (URI/host/extension/filename + regex) with the
       response headers to set when it matches. v-model is the plugin's
       config.rules array. -->
  <div class="response-header-rules">
    <a-button type="dashed" size="small" class="rh-add" @click="openCreate">
      <a-icon type="plus" /> {{ $t('responseHeader.AddRule') }}
    </a-button>

    <a-table v-if="rules.length" :data-source="rows" :columns="columns"
             :pagination="false" row-key="_idx" size="small" class="rh-table">
      <template slot="conditions" slot-scope="text, record">
        <code>{{ summarizeConditions(record) }}</code>
      </template>
      <template slot="headers" slot-scope="text, record">
        <code>{{ summarizeHeaders(record) }}</code>
      </template>
      <template slot="enabled" slot-scope="text, record">
        <a-tag :color="record.enabled === false ? '' : 'green'">
          {{ record.enabled === false ? $t('responseHeader.Disabled') : $t('responseHeader.Enabled') }}
        </a-tag>
      </template>
      <template slot="actions" slot-scope="text, record">
        <a @click="openEdit(record._idx)">{{ $t('responseHeader.Edit') }}</a>
        <a-divider type="vertical" />
        <a class="text-danger" @click="removeRule(record._idx)">{{ $t('responseHeader.Remove') }}</a>
      </template>
    </a-table>
    <p v-else class="rh-empty">{{ $t('responseHeader.NoRules') }}</p>

    <a-modal :visible="modalVisible" :title="$t('responseHeader.RuleModalTitle')"
             :closable="false" :maskClosable="false" width="640px"
             @ok="saveRule" @cancel="closeModal"
             :okButtonProps="{ props: { disabled: !canSave } }">
      <a-form-item :label="$t('responseHeader.RuleName')">
        <a-input v-model="draft.name" :placeholder="$t('responseHeader.RuleNamePlaceholder')" />
      </a-form-item>

      <div class="rh-section-title">{{ $t('responseHeader.Conditions') }}</div>
      <DynamicRule :value="draftConditionValue" @input="onConditions" />

      <div class="rh-section-title">{{ $t('responseHeader.Headers') }}</div>
      <div v-for="(h, i) in draft.headers" :key="i" class="rh-header-row">
        <a-input v-model="h.name" class="rh-h-name" :placeholder="$t('responseHeader.HeaderName')" />
        <span class="rh-colon">:</span>
        <a-input v-model="h.value" class="rh-h-value" :placeholder="$t('responseHeader.HeaderValue')" />
        <a-icon v-if="draft.headers.length > 1" class="rh-remove" type="minus-circle"
                @click="removeHeader(i)" />
      </div>
      <a-button type="dashed" size="small" class="rh-add-header" @click="addHeader">
        <a-icon type="plus" /> {{ $t('responseHeader.AddHeader') }}
      </a-button>

      <a-checkbox v-model="draft.enabled" class="rh-enabled">
        {{ $t('responseHeader.RuleEnabled') }}
      </a-checkbox>
    </a-modal>
  </div>
</template>

<script>
import DynamicRule from '@/views/domain/EditDomain/DynamicRule';

function blankRule() {
  return {
    name: '',
    logic: 'AND',
    negate: false,
    conditions: [{ argument: 'PATH', key: '', operator: '~~', value: '' }],
    headers: [{ name: '', value: '' }],
    enabled: true,
  };
}

export default {
  name: 'ResponseHeaderRules',
  components: { DynamicRule },
  props: {
    // The plugin's config.rules array.
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      rules: Array.isArray(this.value) ? this.value.map((r) => ({ ...r })) : [],
      modalVisible: false,
      editIndex: -1,
      draft: blankRule(),
      columns: [
        { title: this.$t('responseHeader.ColName'), dataIndex: 'name', width: '20%' },
        { title: this.$t('responseHeader.ColWhen'), scopedSlots: { customRender: 'conditions' } },
        { title: this.$t('responseHeader.ColSet'), scopedSlots: { customRender: 'headers' } },
        { title: this.$t('responseHeader.ColEnabled'), scopedSlots: { customRender: 'enabled' }, width: 90 },
        { title: '', scopedSlots: { customRender: 'actions' }, width: 120 },
      ],
    };
  },
  computed: {
    rows() {
      return this.rules.map((r, i) => ({ ...r, _idx: i }));
    },
    // DynamicRule reads/emits { logic, negate, conditions }.
    draftConditionValue() {
      return { logic: this.draft.logic, negate: this.draft.negate, conditions: this.draft.conditions };
    },
    canSave() {
      const hasCond = (this.draft.conditions || []).some((c) => c.value !== '');
      const hasHeader = (this.draft.headers || []).some((h) => h.name !== '');
      return hasCond && hasHeader;
    },
  },
  watch: {
    value(v) {
      this.rules = Array.isArray(v) ? v.map((r) => ({ ...r })) : [];
    },
  },
  methods: {
    summarizeConditions(r) {
      return (r.conditions || [])
        .map((c) => `${c.argument}${c.key ? `[${c.key}]` : ''} ${c.operator} ${c.value}`)
        .join(` ${r.logic} `);
    },
    summarizeHeaders(r) {
      return (r.headers || []).map((h) => `${h.name}: ${h.value}`).join(', ');
    },
    openCreate() {
      this.editIndex = -1;
      this.draft = blankRule();
      this.modalVisible = true;
    },
    openEdit(idx) {
      this.editIndex = idx;
      const r = this.rules[idx] || {};
      const base = blankRule();
      this.draft = {
        name: r.name || '',
        logic: r.logic || 'AND',
        negate: !!r.negate,
        conditions: (r.conditions && r.conditions.length ? r.conditions : base.conditions).map((c) => ({ ...c })),
        headers: (r.headers && r.headers.length ? r.headers : base.headers).map((h) => ({ ...h })),
        enabled: r.enabled !== false,
      };
      this.modalVisible = true;
    },
    onConditions(v) {
      this.draft.logic = v.logic;
      this.draft.negate = v.negate;
      this.draft.conditions = v.conditions;
    },
    addHeader() {
      this.draft.headers.push({ name: '', value: '' });
    },
    removeHeader(i) {
      if (this.draft.headers.length > 1) {
        this.draft.headers.splice(i, 1);
      }
    },
    saveRule() {
      if (!this.canSave) {
        return;
      }
      const rule = {
        name: this.draft.name || 'Response Header Rule',
        logic: this.draft.logic,
        negate: this.draft.negate,
        conditions: this.draft.conditions.filter((c) => c.value !== ''),
        headers: this.draft.headers
          .filter((h) => h.name !== '')
          .map((h) => ({ name: h.name, value: h.value })),
        enabled: this.draft.enabled !== false,
      };
      const next = this.rules.slice();
      if (this.editIndex >= 0) {
        next.splice(this.editIndex, 1, rule);
      } else {
        next.push(rule);
      }
      this.rules = next;
      this.emitChange();
      this.closeModal();
    },
    removeRule(idx) {
      const next = this.rules.slice();
      next.splice(idx, 1);
      this.rules = next;
      this.emitChange();
    },
    closeModal() {
      this.modalVisible = false;
    },
    emitChange() {
      this.$emit('input', this.rules);
    },
  },
};
</script>

<style scoped>
.rh-add { margin-bottom: 12px; }
.rh-empty { color: #999; margin: 8px 0 0 2px; }
.rh-table { margin-top: 4px; }
.rh-section-title { font-weight: 600; color: #555; margin: 14px 0 6px; }
.rh-header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.rh-h-name { flex: 1 1 160px; min-width: 0; }
.rh-h-value { flex: 2 1 200px; min-width: 0; }
.rh-colon { color: #999; }
.rh-remove {
  font-size: 18px;
  color: #bbb;
  cursor: pointer;
  transition: color 0.2s;
  flex: none;
}
.rh-remove:hover { color: #ff4d4f; }
.rh-add-header { margin: 4px 0 14px 0; }
.rh-enabled { display: block; margin-top: 8px; color: #555; }
</style>
